import React from 'react';
import { BellRing, WifiOff } from 'lucide-react';
import { useNow } from '@/hooks/useNow';
import { serverNow } from '@/lib/serverClock';
import type { LiveOverview } from '@/services/liveOverviewService';
import type { StaffNotification } from '@/services/notificationService';
import type { CallbackTicket } from '@/services/callbackTicketService';
import type { NotificationsContextValue } from '@/contexts/NotificationsContext';
import type { LiveConnectionStatus } from '@/contexts/LiveEventsContext';
import { LiveNowSection } from './LiveNowSection';
import { NotificationList } from './NotificationList';
import { SoundSettings } from './SoundSettings';

interface NotificationPanelProps {
  notifications: NotificationsContextValue;
  overview: LiveOverview;
  overviewLoading: boolean;
  status: LiveConnectionStatus;
  onOpenContact: (contactKey: string) => void;
  onOpenCall: (callSid: string) => void;
  onOpenTicket: (ticket: CallbackTicket) => void;
  onOpenNotification: (item: StaffNotification) => void;
}

const TICK_MS = 1_000;

/** Several agent numbers in play: rows say which one the customer used (plan §6.13). */
function usesSeveralAgentNumbers(overview: LiveOverview, items: StaffNotification[]): boolean {
  const numbers = new Set<string>();
  const add = (n: string | null | undefined) => {
    const digits = (n ?? '').replace(/\D/g, '');
    if (digits) numbers.add(digits);
  };
  overview.waiting.forEach((w) => add(w.agent_number));
  overview.live_calls.forEach((c) => add(c.agent_number));
  overview.callback_tickets.forEach((t) => add(t.agent_number));
  items.forEach((i) => add(i.agent_number));
  return numbers.size > 1;
}

function LoadingRows() {
  return (
    <div className="space-y-2 px-2.5 py-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-7 w-7 rounded-lg bg-muted motion-safe:animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-3/4 rounded bg-muted motion-safe:animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted motion-safe:animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center px-6 py-8 text-center">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-soft text-brand" aria-hidden="true">
        <BellRing className="h-5 w-5" />
      </span>
      <p className="mt-3 text-sm font-semibold">Alles ruhig</p>
      <p className="mt-1 max-w-[260px] text-xs leading-5 text-muted-foreground">
        Schreibt jemand auf WhatsApp, wünscht sich einen Mitarbeiter oder läuft ein Anruf, erscheint es hier.
      </p>
    </div>
  );
}

/** Content of the bell's popover: „Jetzt live“, „Benachrichtigungen“, sound settings. */
export function NotificationPanel({
  notifications,
  overview,
  overviewLoading,
  status,
  onOpenContact,
  onOpenCall,
  onOpenTicket,
  onOpenNotification,
}: NotificationPanelProps) {
  const now = serverNow(useNow(TICK_MS));
  const { items, unreadCount, loading } = notifications;
  const hasLive =
    overview.waiting.length +
      overview.callback_tickets.length +
      overview.live_calls.length +
      overview.active_chats.length >
    0;
  const showAgentNumber = usesSeveralAgentNumbers(overview, items);
  const isLoading = (loading || overviewLoading) && items.length === 0 && !hasLive;

  return (
    <div className="flex max-h-[min(640px,var(--radix-popover-content-available-height,640px))] flex-col">
      {status === 'degraded' && (
        <p
          role="status"
          className="mx-2.5 mt-2.5 flex items-center gap-2 rounded-lg bg-muted px-2.5 py-1.5 text-xs text-muted-foreground"
        >
          <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Verbindung unterbrochen – aktualisiert alle 5 s
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 pb-2">
        {isLoading ? (
          <LoadingRows />
        ) : !hasLive && items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <LiveNowSection
              overview={overview}
              now={now}
              onOpenContact={onOpenContact}
              onOpenCall={onOpenCall}
              onOpenTicket={onOpenTicket}
              showAgentNumber={showAgentNumber}
            />
            <NotificationList
              items={items}
              now={now}
              unreadCount={unreadCount}
              onOpen={onOpenNotification}
              onMarkAllRead={() => void notifications.markRead('all')}
              showAgentNumber={showAgentNumber}
            />
          </>
        )}
      </div>
      <SoundSettings
        prefs={notifications.prefs}
        onChange={notifications.updatePrefs}
        onPreview={notifications.previewChime}
        soundBlocked={notifications.soundBlocked}
        onEnableSound={() => void notifications.enableSound()}
        desktopPermission={notifications.desktopPermission}
        onRequestDesktop={notifications.requestDesktop}
      />
    </div>
  );
}

export default NotificationPanel;
